import { OwcIconButton, OwcListItem, OwcMenu, OwcTooltip } from "@one/react";
import moment from "moment-timezone";
import { MouseEvent, ReactNode, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { RootStore } from "../../reducers";
import { BookingRewireDataType } from "../../Models/BookingRewireDataType";
import { getLogParamsList } from "../bookings/BookingMenuAction";

const CoverMenuItem = styled.div`
  height: 0.973480224609375px;
  width: 100%;
  border-radius: 0;
  background: var(--one-color-gray-300);
`;

type BookingMenuActionParams = {
  params: {
    data: BookingRewireDataType;
  };
  handlers: {
    deleteEquipment: () => void;
    editBooking: (data: BookingRewireDataType) => void;
    setRowEquipmentId: React.Dispatch<React.SetStateAction<string>>;
    setPopupEnableStatus: React.Dispatch<React.SetStateAction<boolean>>;
    rejectEquipment?: () => void;
    setRejectRowEquipmentId?: React.Dispatch<React.SetStateAction<string>>;
  };
};

const BookingMenuAction = ({
  params, // eslint-disable-next-line no-unused-vars
  handlers: { deleteEquipment, editBooking, setRowEquipmentId, setPopupEnableStatus, rejectEquipment, setRejectRowEquipmentId },
}: BookingMenuActionParams) => {
  const user = useSelector((store: RootStore) => store.user);
  const [anchorEl, setAnchorEl] = useState<HTMLOwcIconButtonElement | null>(null);
  const uniqueAnchorId = `my-booking-menu-${params?.data?.id}`;
  const currentTimeString = moment.tz(user.timezone).utc().format();

  const handleSettingClose = () => {
    setAnchorEl(null);
  };
  const handleSettingClick = (
    event: MouseEvent<HTMLOwcIconButtonElement, globalThis.MouseEvent>,
    params: { data: BookingRewireDataType }
  ) => {
    setRowEquipmentId?.(params?.data?.id);
    setAnchorEl(event.currentTarget);
  };

  const userDoesNotHaveRightsToWriteInLogbook =
    !user.roles.includes("LogbookAdmin") && !user.roles.includes("LogbookWriter");

  return (
    <>
      <OwcIconButton
        icon="more_vertical"
        type="legacy"
        flat
        onClick={(event) => {
          handleSettingClick(event, params);
        }}
        id={uniqueAnchorId}
        data-testid="booking-menu-toggle"
        disabled={!params?.data?.active}
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      />
      {ReactDOM.createPortal(
        <OwcMenu
          data-testid="simple-menu"
          scrollPadding={false}
          anchor={uniqueAnchorId}
          visible={!!anchorEl}
          onClickOutside={handleSettingClose}
          disablePortal={true}
          placeholder={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        >
          <OwcListItem
            data-testid="simple-menu-filter"
            onClick={() => {
              setAnchorEl(null);
              setPopupEnableStatus(true);
              editBooking(params?.data);
            }}
            disabled={
              !params?.data?.active ||
              params?.data?.checkInDone ||
              (params?.data?.checkInRequired && params.data.dateFrom <= currentTimeString) ||
              params?.data?.dateTo < currentTimeString
            }
            placeholder={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          >
            Edit
          </OwcListItem>
          <div id={`AddLogMenuOption-${ params?.data?.id }`}>
            <OwcListItem
              data-testid="simple-menu-filter"
              onClick={() => {
                setAnchorEl(null);
                const logParamsList = getLogParamsList(params.data);
                logParamsList.forEach((url) => {
                  window.open(`${process.env.REACT_APP_LOGBOOK_APP_URL}${url}`, "_blank");
                });
              }}
              disabled={userDoesNotHaveRightsToWriteInLogbook}
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            >
              Add log
            </OwcListItem>

            {userDoesNotHaveRightsToWriteInLogbook && (
              <OwcTooltip
                anchor={`AddLogMenuOption-${ params?.data?.id }`}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                You do not have permissions to create log entry.
              </OwcTooltip>
            )}
          </div>
          <OwcListItem
            data-testid="simple-menu-duplicate"
            onClick={() => {
              setAnchorEl(null);
              setPopupEnableStatus(true);
              const duplicated = { ...params.data, isDuplicate: true };

              editBooking(duplicated);
            }}
            disabled={!params?.data?.active}
          >
            Duplicate
          </OwcListItem>
          <CoverMenuItem />
          <OwcListItem
            data-testid="simple-menu-filter"
            onClick={() => {
              setAnchorEl(null);
              deleteEquipment();
            }}
            disabled={
              !params?.data?.active ||
              params?.data?.dateFrom < currentTimeString ||
              params?.data?.dateTo < currentTimeString
            }
            placeholder={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          >
            Cancel
          </OwcListItem>
        </OwcMenu> as ReactNode,
        document.body
      )}
    </>
  );
};
export default BookingMenuAction;
